import { Avatar, Box, Button, Typography } from '@mui/material';
import type { SxProps } from '@mui/system';
import type { FC, ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { Accept, DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Upload as UploadIcon } from 'src/icons/upload';

interface Props extends DropzoneOptions {
  sx?: SxProps;
  accept: Accept;
  maxFiles?: number;
  maxSize?: number;
  minSize?: number;
  title?: string;
  description?: string;
  icon?: ReactNode;
  onDrop: <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent,
  ) => void;
}

export const FileDropzone: FC<Props> = ({
  accept,
  maxFiles,
  maxSize,
  minSize,
  onDrop,
  sx,
  title,
  description,
  icon,
}) => {
  const { t } = useTranslation(['common']);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onDrop,
  });

  return (
    <Box
      sx={{
        alignItems: 'center',
        borderColor: 'neutral.200',
        borderRadius: 1,
        borderStyle: 'dashed',
        borderWidth: 1,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'center',
        outline: 'none',
        p: 2,
        ...(isDragActive && {
          borderColor: 'primary.main',
          backgroundColor: 'action.hover',
        }),
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: 'action.hover',
        },
        ...sx,
      }}
      {...getRootProps()}>
      <input {...getInputProps()} />
      <Avatar
        sx={{
          backgroundColor: '#E1E3EA',
          color: 'text.secondary',
          height: 36,
          width: 36,
        }}>
        {icon ?? <UploadIcon />}
      </Avatar>
      <Button color="primary" variant="text">
        {title ?? t('common:browse')}
      </Button>
      <Typography align="center" sx={{ color: 'text.secondary' }} variant="caption">
        {description ?? t('common:dropFile')}
      </Typography>
    </Box>
  );
};
